import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout, { LegalSection } from '../legal/LegalPageLayout';

const PrivacyPolicy = () => (
    <LegalPageLayout title="Privacy Policy" effectiveDate="August 10, 2026" path="/privacy">
    <LegalSection heading="1. Overview">
      <p>
        This Privacy Policy explains how WebForms.biz ("WebForms", "we", "us") collects, uses,
        and shares personal information when you visit our website, create an account, or use
        our form-building platform (the "Service"). It also explains the choices and rights you
        have. This Policy should be read together with our{' '}
        <Link to="/terms" className="text-primary hover:text-primary/80 font-medium">Terms of Service</Link>.
      </p>
      <p>
        Two roles matter here: for account holders ("customers") and website visitors, WebForms
        is the data controller. For data that respondents submit through forms our customers
        build ("form submissions"), the customer is the controller and WebForms processes that
        data on the customer's behalf. If you submitted information through a form powered by
        WebForms, please direct privacy requests to the organization that created the form; we
        will assist them in responding.
      </p>
    </LegalSection>

    <LegalSection heading="2. Information We Collect">
      <p><strong className="text-foreground">Information you provide:</strong></p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Account data — name, email address, password (stored hashed), and plan details.</li>
        <li>Mobile number — if you choose to provide it for customer service text messages.</li>
        <li>Billing data — handled by our payment processor, Stripe. We do not store full card numbers.</li>
        <li>Content — forms you build, files you upload, and support messages you send us.</li>
      </ul>
      <p><strong className="text-foreground">Information collected automatically:</strong></p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Usage data — pages viewed, features used, timestamps, and referring pages.</li>
        <li>Device data — IP address, browser type, operating system, and screen size.</li>
        <li>Cookies and similar technologies used for authentication, preferences, and analytics (see Section 7).</li>
      </ul>
      <p><strong className="text-foreground">Form submissions (processed on behalf of customers):</strong></p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Whatever a respondent enters into a customer's form, plus limited technical metadata (such as submission time and IP address) used for security and spam prevention.</li>
      </ul>
    </LegalSection>

    <LegalSection heading="3. How We Use Information">
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Provide, operate, and secure the Service, including authentication and fraud/spam prevention.</li>
        <li>Process payments and manage subscriptions.</li>
        <li>Respond to support requests and communicate about the Service (including service notices).</li>
        <li>Understand usage to improve features, performance, and reliability.</li>
        <li>Comply with legal obligations and enforce our Terms.</li>
      </ul>
      <p>
        Where GDPR or similar laws apply, we rely on the following legal bases: performance of
        a contract (providing the Service), legitimate interests (security, improvement,
        communications), consent (where required, e.g., certain cookies or marketing), and
        legal obligation.
      </p>
      <p>
        We do not sell personal information, and we do not use form submissions to train
        advertising profiles or market to your respondents.
      </p>
    </LegalSection>

    <LegalSection heading="4. How We Share Information">
      <p>We share personal information only with:</p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li><strong className="text-foreground">Service providers</strong> that help us run the Service — hosting and database infrastructure (e.g., Supabase), payment processing (Stripe), and email delivery — under contracts limiting their use of the data.</li>
        <li><strong className="text-foreground">Integrations you enable</strong> — if you connect your forms to third-party tools, we share the data needed to perform that integration at your direction.</li>
        <li><strong className="text-foreground">Legal and safety</strong> — when required by law, legal process, or to protect the rights, safety, and property of WebForms, our users, or the public.</li>
        <li><strong className="text-foreground">Business transfers</strong> — in connection with a merger, acquisition, or sale of assets, subject to this Policy.</li>
      </ul>
    </LegalSection>

    <LegalSection heading="5. Data Retention">
      <p>
        We keep account data for as long as your account is active and as needed to comply with
        legal obligations. Form submissions are retained under the customer's control — customers
        can delete individual submissions or entire forms at any time. When an account is
        terminated, content becomes available for export for 30 days and is then deleted from
        active systems, except where retention is required by law. Backups are purged on a
        rolling schedule.
      </p>
    </LegalSection>

    <LegalSection heading="6. Security">
      <p>
        We use administrative, technical, and organizational measures designed to protect
        personal information, including encryption in transit (TLS), encryption at rest for our
        databases, access controls, and row-level security on customer data. No system is 100%
        secure; please use a strong, unique password and keep your credentials confidential.
      </p>
    </LegalSection>

    <LegalSection heading="7. Cookies">
      <p>
        We use strictly necessary cookies for sign-in sessions and security, preference cookies
        to remember settings, and limited analytics to understand aggregate usage of our own
        website. You can control cookies through your browser settings; disabling necessary
        cookies may prevent you from signing in.
      </p>
    </LegalSection>

    <LegalSection heading="8. International Transfers">
      <p>
        Our infrastructure is primarily located in the United States. Where we transfer
        personal information from the EEA, UK, or Switzerland, we rely on appropriate
        safeguards such as Standard Contractual Clauses with our service providers.
      </p>
    </LegalSection>

    <LegalSection heading="9. Your Rights">
      <p>
        Depending on where you live (including under GDPR and the CCPA/CPRA), you may have the
        right to:
      </p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>access the personal information we hold about you and receive a copy;</li>
        <li>correct inaccurate information;</li>
        <li>delete your information;</li>
        <li>object to or restrict certain processing, and withdraw consent where processing is based on consent;</li>
        <li>data portability;</li>
        <li>not be discriminated against for exercising these rights.</li>
      </ul>
      <p>
        To exercise these rights for your account data, use your account settings or contact us
        at{' '}
        <a href="mailto:privacy@webforms.biz" className="text-primary hover:text-primary/80 font-medium">privacy@webforms.biz</a>.
        We will verify your request and respond within the time required by applicable law. If
        your request concerns data submitted through a customer's form, we will refer it to
        that customer and assist them as their processor. You may also lodge a complaint with
        your local data-protection authority.
      </p>
    </LegalSection>

    <LegalSection heading="10. SMS Communications">
      <p>
        If you provide your mobile number and consent to receive customer service text messages
        from TextCallBack, we may use that number to send messages related to your support
        requests and our services. Message frequency may vary. Message and data rates may apply.
        Consent is not a condition of purchasing goods or services.
      </p>
      <p>
        You can opt out at any time by replying <strong className="text-foreground">STOP</strong>.
        For help, reply <strong className="text-foreground">HELP</strong> or contact us using the
        details in Section 14. We may retain your mobile number, consent record, and opt-out
        request as needed to administer these communications and comply with applicable law.
      </p>
      <p>
        We do not sell or share mobile telephone numbers with third parties or affiliates for
        their own marketing purposes. We may share a mobile number with service providers only
        when needed to deliver the customer service text messages you requested, and those
        providers are contractually restricted from using it for their own marketing.
      </p>
    </LegalSection>

    <LegalSection heading="11. Children">
      <p>
        The Service is not directed to children under 16, and we do not knowingly collect
        personal information from them as account holders. Customers are responsible for
        ensuring their forms comply with laws applicable to their respondents.
      </p>
    </LegalSection>

    <LegalSection heading="12. Data Processing for Customers">
      <p>
        For customers who need contractual processing terms (including GDPR Article 28
        obligations and CCPA service-provider commitments), a data processing addendum is
        available on request at{' '}
        <a href="mailto:privacy@webforms.biz" className="text-primary hover:text-primary/80 font-medium">privacy@webforms.biz</a>.
      </p>
    </LegalSection>

    <LegalSection heading="13. Changes to This Policy">
      <p>
        We may update this Policy from time to time. If a change is material, we will provide
        reasonable notice (for example, by email or an in-app notice) before it takes effect.
        The "Effective date" above reflects the latest revision.
      </p>
    </LegalSection>

    <LegalSection heading="14. Contact Us">
      <p>
        For privacy questions or requests, contact{' '}
        <a href="mailto:privacy@webforms.biz" className="text-primary hover:text-primary/80 font-medium">privacy@webforms.biz</a>.
      </p>
    </LegalSection>
  </LegalPageLayout>
);

export default PrivacyPolicy;
